// frontend/src/data/equipmentCatalogData.ts
import type { Category, Subcategory, Brand, Model } from '@/types/equipmentCatalog';

/**
 * Equipment catalog categories.
 * Order determines display sequence in the wizard.
 */
export const CATEGORIES: Category[] = [
  { id: 'circulation', name: 'Circulation', order: 1 },
  { id: 'lighting', name: 'Lighting', order: 2 },
  { id: 'filtration', name: 'Filtration', order: 3 },
  { id: 'control', name: 'Control', order: 4 },
  { id: 'other', name: 'Other', order: 5 },
];

/**
 * Equipment subcategories within each category.
 * allowMultiple: true = users commonly have multiple (e.g., wavemakers)
 * allowMultiple: false = users typically have one (e.g., return pump, skimmer)
 */
export const SUBCATEGORIES: Subcategory[] = [
  // Circulation
  { id: 'return_pump', categoryId: 'circulation', name: 'Return Pump', allowMultiple: false, order: 1 },
  { id: 'wavemaker', categoryId: 'circulation', name: 'Wavemaker', allowMultiple: true, order: 2 },

  // Lighting
  { id: 'main_light', categoryId: 'lighting', name: 'Main Light', allowMultiple: true, order: 1 },
  { id: 'refugium_light', categoryId: 'lighting', name: 'Refugium Light', allowMultiple: false, order: 2 },

  // Filtration
  { id: 'skimmer', categoryId: 'filtration', name: 'Protein Skimmer', allowMultiple: false, order: 1 },
  { id: 'filter_roller', categoryId: 'filtration', name: 'Filter Roller', allowMultiple: false, order: 2 },
  { id: 'reactor', categoryId: 'filtration', name: 'Reactor', allowMultiple: true, order: 3 },
  { id: 'ats', categoryId: 'filtration', name: 'Algae Turf Scrubber', allowMultiple: false, order: 4 },

  // Control
  { id: 'controller', categoryId: 'control', name: 'Controller', allowMultiple: false, order: 1 },
  { id: 'doser', categoryId: 'control', name: 'Doser', allowMultiple: false, order: 2 },
  { id: 'ato', categoryId: 'control', name: 'ATO', allowMultiple: false, order: 3 },
  { id: 'automated_testing', categoryId: 'control', name: 'Automated Testing', allowMultiple: false, order: 4 },

  // Other
  { id: 'heater', categoryId: 'other', name: 'Heater', allowMultiple: true, order: 1 },
  { id: 'chiller', categoryId: 'other', name: 'Chiller', allowMultiple: false, order: 2 },
  { id: 'uv_sterilizer', categoryId: 'other', name: 'UV Sterilizer', allowMultiple: false, order: 3 },
  { id: 'ozone', categoryId: 'other', name: 'Ozone Generator', allowMultiple: false, order: 4 },
];

/**
 * Equipment brands.
 * These are referenced by models via brandId.
 */
export const BRANDS: Brand[] = [
  // Pumps & Flow
  { id: 'ecotech', name: 'EcoTech Marine' },
  { id: 'sicce', name: 'Sicce' },
  { id: 'reef_octopus', name: 'Reef Octopus' },
  { id: 'red_sea', name: 'Red Sea' },
  { id: 'jebao', name: 'Jebao' },
  { id: 'tunze', name: 'Tunze' },
  { id: 'maxspect', name: 'Maxspect' },

  // Lighting
  { id: 'ai', name: 'Aqua Illumination' },
  { id: 'kessil', name: 'Kessil' },
  { id: 'orphek', name: 'Orphek' },

  // Filtration
  { id: 'nyos', name: 'NYOS' },
  { id: 'bubble_magus', name: 'Bubble Magus' },
  { id: 'clarisea', name: 'Clarisea' },
  { id: 'theiling', name: 'Theiling' },
  { id: 'pax_bellum', name: 'Pax Bellum' },
  { id: 'santa_monica', name: 'Santa Monica Filtration' },
  { id: 'brs', name: 'BRS' },
  { id: 'two_little_fishies', name: 'Two Little Fishies' },

  // Controllers
  { id: 'neptune', name: 'Neptune Systems' },
  { id: 'ghl', name: 'GHL' },
  { id: 'hydros', name: 'Hydros' },

  // Dosing & Testing
  { id: 'focustronic', name: 'Focustronic' },
  { id: 'kamoer', name: 'Kamoer' },
  { id: 'aquawiz', name: 'AquaWiz' },
  { id: 'aquarimate', name: 'Aquarimate' },

  // ATO
  { id: 'xp_aqua', name: 'XP Aqua' },
  { id: 'smart_ato', name: 'AutoAqua' },
  { id: 'icecap', name: 'IceCap' },

  // Heating/Cooling
  { id: 'eheim', name: 'Eheim' },
  { id: 'finnex', name: 'Finnex' },
  { id: 'teco', name: 'TECO' },
  { id: 'jbj', name: 'JBJ' },
  { id: 'aqua_euro', name: 'Aqua Euro' },

  // UV/Ozone
  { id: 'aqua_uv', name: 'Aqua Ultraviolet' },
  { id: 'coralife', name: 'Coralife' },
  { id: 'ozotech', name: 'Ozotech' },
];

/**
 * Equipment models.
 * Each model belongs to a brand and subcategory.
 */
export const MODELS: Model[] = [
  // === RETURN PUMPS ===
  // EcoTech Vectra
  { id: 'vectra-s2', brandId: 'ecotech', subcategoryId: 'return_pump', name: 'Vectra S2' },
  { id: 'vectra-m2', brandId: 'ecotech', subcategoryId: 'return_pump', name: 'Vectra M2' },
  { id: 'vectra-l2', brandId: 'ecotech', subcategoryId: 'return_pump', name: 'Vectra L2' },
  // Sicce Syncra
  { id: 'syncra-sdc-6', brandId: 'sicce', subcategoryId: 'return_pump', name: 'Syncra SDC 6.0' },
  { id: 'syncra-sdc-7', brandId: 'sicce', subcategoryId: 'return_pump', name: 'Syncra SDC 7.0' },
  { id: 'syncra-sdc-9', brandId: 'sicce', subcategoryId: 'return_pump', name: 'Syncra SDC 9.0' },
  // Reef Octopus VarioS
  { id: 'varios-2', brandId: 'reef_octopus', subcategoryId: 'return_pump', name: 'VarioS-2' },
  { id: 'varios-4', brandId: 'reef_octopus', subcategoryId: 'return_pump', name: 'VarioS-4' },
  { id: 'varios-6', brandId: 'reef_octopus', subcategoryId: 'return_pump', name: 'VarioS-6' },
  // Red Sea ReefRun
  { id: 'reefrun-900', brandId: 'red_sea', subcategoryId: 'return_pump', name: 'ReefRun DC 900' },
  { id: 'reefrun-1200', brandId: 'red_sea', subcategoryId: 'return_pump', name: 'ReefRun DC 1200' },
  // Jebao DCP
  { id: 'dcp-3000', brandId: 'jebao', subcategoryId: 'return_pump', name: 'DCP-3000' },
  { id: 'dcp-5000', brandId: 'jebao', subcategoryId: 'return_pump', name: 'DCP-5000' },
  { id: 'dcp-8000', brandId: 'jebao', subcategoryId: 'return_pump', name: 'DCP-8000' },

  // === WAVEMAKERS ===
  // EcoTech VorTech
  { id: 'mp10', brandId: 'ecotech', subcategoryId: 'wavemaker', name: 'VorTech MP10' },
  { id: 'mp40', brandId: 'ecotech', subcategoryId: 'wavemaker', name: 'VorTech MP40' },
  { id: 'mp60', brandId: 'ecotech', subcategoryId: 'wavemaker', name: 'VorTech MP60' },
  // AI Nero
  { id: 'nero-3', brandId: 'ai', subcategoryId: 'wavemaker', name: 'Nero 3' },
  { id: 'nero-5', brandId: 'ai', subcategoryId: 'wavemaker', name: 'Nero 5' },
  { id: 'nero-7', brandId: 'ai', subcategoryId: 'wavemaker', name: 'Nero 7' },
  // Tunze Turbelle
  { id: 'turbelle-6040', brandId: 'tunze', subcategoryId: 'wavemaker', name: 'Turbelle 6040' },
  { id: 'turbelle-6055', brandId: 'tunze', subcategoryId: 'wavemaker', name: 'Turbelle 6055' },
  { id: 'turbelle-6095', brandId: 'tunze', subcategoryId: 'wavemaker', name: 'Turbelle 6095' },
  // Maxspect Gyre
  { id: 'gyre-xf150', brandId: 'maxspect', subcategoryId: 'wavemaker', name: 'Gyre XF150' },
  { id: 'gyre-xf250', brandId: 'maxspect', subcategoryId: 'wavemaker', name: 'Gyre XF250' },
  { id: 'gyre-xf350', brandId: 'maxspect', subcategoryId: 'wavemaker', name: 'Gyre XF350' },
  // Jebao SOW
  { id: 'sow-8', brandId: 'jebao', subcategoryId: 'wavemaker', name: 'SOW-8' },
  { id: 'sow-16', brandId: 'jebao', subcategoryId: 'wavemaker', name: 'SOW-16' },
  { id: 'sow-20', brandId: 'jebao', subcategoryId: 'wavemaker', name: 'SOW-20' },

  // === MAIN LIGHTS ===
  // AI Hydra/Prime
  { id: 'prime-16-hd', brandId: 'ai', subcategoryId: 'main_light', name: 'Prime 16 HD' },
  { id: 'hydra-32-hd', brandId: 'ai', subcategoryId: 'main_light', name: 'Hydra 32 HD' },
  { id: 'hydra-64-hd', brandId: 'ai', subcategoryId: 'main_light', name: 'Hydra 64 HD' },
  // EcoTech Radion
  { id: 'radion-xr15-g6', brandId: 'ecotech', subcategoryId: 'main_light', name: 'Radion XR15 G6' },
  { id: 'radion-xr30-g6', brandId: 'ecotech', subcategoryId: 'main_light', name: 'Radion XR30 G6' },
  // Kessil
  { id: 'a360x', brandId: 'kessil', subcategoryId: 'main_light', name: 'A360X' },
  { id: 'a500x', brandId: 'kessil', subcategoryId: 'main_light', name: 'A500X' },
  { id: 'ap700', brandId: 'kessil', subcategoryId: 'main_light', name: 'AP700' },
  // Orphek
  { id: 'atlantik-v4', brandId: 'orphek', subcategoryId: 'main_light', name: 'Atlantik V4' },
  { id: 'or3-120', brandId: 'orphek', subcategoryId: 'main_light', name: 'OR3 120' },
  // Red Sea ReefLED
  { id: 'reefled-90', brandId: 'red_sea', subcategoryId: 'main_light', name: 'ReefLED 90' },
  { id: 'reefled-160', brandId: 'red_sea', subcategoryId: 'main_light', name: 'ReefLED 160' },

  // === REFUGIUM LIGHTS ===
  { id: 'kessil-h80', brandId: 'kessil', subcategoryId: 'refugium_light', name: 'H80 Tuna Flora' },
  { id: 'kessil-h160', brandId: 'kessil', subcategoryId: 'refugium_light', name: 'H160 Tuna Flora' },
  { id: 'ai-prime-fuge', brandId: 'ai', subcategoryId: 'refugium_light', name: 'Prime Fuge' },
  { id: 'tunze-eco-chic', brandId: 'tunze', subcategoryId: 'refugium_light', name: 'Eco Chic' },
  { id: 'innovative-marine-chaetomax', brandId: 'icecap', subcategoryId: 'refugium_light', name: 'ChaetoMax' },

  // === SKIMMERS ===
  // Reef Octopus
  { id: 'regal-150s', brandId: 'reef_octopus', subcategoryId: 'skimmer', name: 'Regal 150-S' },
  { id: 'regal-200s', brandId: 'reef_octopus', subcategoryId: 'skimmer', name: 'Regal 200-S' },
  { id: 'regal-250s', brandId: 'reef_octopus', subcategoryId: 'skimmer', name: 'Regal 250-S' },
  // NYOS Quantum
  { id: 'quantum-120', brandId: 'nyos', subcategoryId: 'skimmer', name: 'Quantum 120' },
  { id: 'quantum-160', brandId: 'nyos', subcategoryId: 'skimmer', name: 'Quantum 160' },
  { id: 'quantum-220', brandId: 'nyos', subcategoryId: 'skimmer', name: 'Quantum 220' },
  // Bubble Magus
  { id: 'curve-5', brandId: 'bubble_magus', subcategoryId: 'skimmer', name: 'Curve 5' },
  { id: 'curve-7', brandId: 'bubble_magus', subcategoryId: 'skimmer', name: 'Curve 7' },
  { id: 'curve-9', brandId: 'bubble_magus', subcategoryId: 'skimmer', name: 'Curve 9' },
  // Red Sea RSK
  { id: 'rsk-300', brandId: 'red_sea', subcategoryId: 'skimmer', name: 'RSK-300' },
  { id: 'rsk-600', brandId: 'red_sea', subcategoryId: 'skimmer', name: 'RSK-600' },

  // === FILTER ROLLERS ===
  { id: 'clarisea-sk-3000', brandId: 'clarisea', subcategoryId: 'filter_roller', name: 'SK-3000' },
  { id: 'clarisea-sk-5000', brandId: 'clarisea', subcategoryId: 'filter_roller', name: 'SK-5000' },
  { id: 'theiling-rollermat', brandId: 'theiling', subcategoryId: 'filter_roller', name: 'Rollermat' },
  { id: 'theiling-rollermat-compact', brandId: 'theiling', subcategoryId: 'filter_roller', name: 'Rollermat Compact' },
  { id: 'reef-octopus-roller', brandId: 'reef_octopus', subcategoryId: 'filter_roller', name: 'Automatic Filter Roller' },

  // === REACTORS ===
  { id: 'brs-reactor-single', brandId: 'brs', subcategoryId: 'reactor', name: 'Single Reactor' },
  { id: 'brs-reactor-dual', brandId: 'brs', subcategoryId: 'reactor', name: 'Dual Reactor' },
  { id: 'tlf-phosban-150', brandId: 'two_little_fishies', subcategoryId: 'reactor', name: 'PhosBan Reactor 150' },
  { id: 'tlf-phosban-550', brandId: 'two_little_fishies', subcategoryId: 'reactor', name: 'PhosBan Reactor 550' },
  { id: 'reef-octopus-bio-150', brandId: 'reef_octopus', subcategoryId: 'reactor', name: 'BioReact 150' },
  { id: 'reef-octopus-bio-200', brandId: 'reef_octopus', subcategoryId: 'reactor', name: 'BioReact 200' },

  // === ATS ===
  { id: 'pax-bellum-arid', brandId: 'pax_bellum', subcategoryId: 'ats', name: 'ARID C18' },
  { id: 'pax-bellum-arid-n18', brandId: 'pax_bellum', subcategoryId: 'ats', name: 'ARID N18' },
  { id: 'santa-monica-drop-1', brandId: 'santa_monica', subcategoryId: 'ats', name: 'DROP 1.2' },
  { id: 'santa-monica-surf-2', brandId: 'santa_monica', subcategoryId: 'ats', name: 'SURF2 Upflow' },

  // === CONTROLLERS ===
  // Neptune Apex
  { id: 'apex-el', brandId: 'neptune', subcategoryId: 'controller', name: 'Apex EL' },
  { id: 'apex-2016', brandId: 'neptune', subcategoryId: 'controller', name: 'Apex 2016' },
  { id: 'apex-a3', brandId: 'neptune', subcategoryId: 'controller', name: 'Apex A3' },
  // GHL
  { id: 'profilux-4', brandId: 'ghl', subcategoryId: 'controller', name: 'ProfiLux 4' },
  { id: 'profilux-4e', brandId: 'ghl', subcategoryId: 'controller', name: 'ProfiLux 4e' },
  { id: 'profilux-mini', brandId: 'ghl', subcategoryId: 'controller', name: 'ProfiLux Mini' },
  // Hydros
  { id: 'hydros-control-2', brandId: 'hydros', subcategoryId: 'controller', name: 'Control 2' },
  { id: 'hydros-control-4', brandId: 'hydros', subcategoryId: 'controller', name: 'Control 4' },
  { id: 'hydros-control-x4', brandId: 'hydros', subcategoryId: 'controller', name: 'Control X4' },

  // === DOSERS ===
  // Focustronic
  { id: 'dosetronic', brandId: 'focustronic', subcategoryId: 'doser', name: 'Dosetronic' },
  // Kamoer
  { id: 'kamoer-x1', brandId: 'kamoer', subcategoryId: 'doser', name: 'X1 Pro' },
  { id: 'kamoer-x4', brandId: 'kamoer', subcategoryId: 'doser', name: 'X4 Pro' },
  // GHL Doser
  { id: 'ghl-doser-2.1', brandId: 'ghl', subcategoryId: 'doser', name: 'Doser 2.1' },
  { id: 'ghl-doser-maxi', brandId: 'ghl', subcategoryId: 'doser', name: 'Doser Maxi' },
  // Neptune DOS
  { id: 'neptune-dos', brandId: 'neptune', subcategoryId: 'doser', name: 'DOS' },
  { id: 'neptune-trident', brandId: 'neptune', subcategoryId: 'doser', name: 'Trident Doser' },
  // Jebao
  { id: 'jebao-dp-4', brandId: 'jebao', subcategoryId: 'doser', name: 'DP-4' },

  // === ATO ===
  { id: 'smart-ato-micro', brandId: 'smart_ato', subcategoryId: 'ato', name: 'Smart ATO Micro' },
  { id: 'smart-ato-duo', brandId: 'smart_ato', subcategoryId: 'ato', name: 'Smart ATO Duo' },
  { id: 'xp-aqua-duetto', brandId: 'xp_aqua', subcategoryId: 'ato', name: 'Duetto ATO' },
  { id: 'tunze-osmolator-3155', brandId: 'tunze', subcategoryId: 'ato', name: 'Osmolator 3155' },
  { id: 'tunze-osmolator-universal', brandId: 'tunze', subcategoryId: 'ato', name: 'Osmolator Universal' },
  { id: 'icecap-ato', brandId: 'icecap', subcategoryId: 'ato', name: 'ATO' },

  // === AUTOMATED TESTING ===
  { id: 'mastertronic', brandId: 'focustronic', subcategoryId: 'automated_testing', name: 'Mastertronic' },
  { id: 'trident', brandId: 'neptune', subcategoryId: 'automated_testing', name: 'Trident' },
  { id: 'kh1', brandId: 'aquawiz', subcategoryId: 'automated_testing', name: 'KH1' },
  { id: 'aquarimate-pro', brandId: 'aquarimate', subcategoryId: 'automated_testing', name: 'Pro' },
  { id: 'ghl-kh-director', brandId: 'ghl', subcategoryId: 'automated_testing', name: 'KH Director' },
  { id: 'alkatronic', brandId: 'focustronic', subcategoryId: 'automated_testing', name: 'Alkatronic' },

  // === HEATERS ===
  { id: 'eheim-jager-150', brandId: 'eheim', subcategoryId: 'heater', name: 'Jager 150W' },
  { id: 'eheim-jager-200', brandId: 'eheim', subcategoryId: 'heater', name: 'Jager 200W' },
  { id: 'eheim-jager-300', brandId: 'eheim', subcategoryId: 'heater', name: 'Jager 300W' },
  { id: 'finnex-hma-200', brandId: 'finnex', subcategoryId: 'heater', name: 'HMA 200W' },
  { id: 'finnex-hma-300', brandId: 'finnex', subcategoryId: 'heater', name: 'HMA 300W' },
  { id: 'finnex-hma-500', brandId: 'finnex', subcategoryId: 'heater', name: 'HMA 500W' },
  { id: 'brs-titanium-300', brandId: 'brs', subcategoryId: 'heater', name: 'Titanium 300W' },
  { id: 'brs-titanium-500', brandId: 'brs', subcategoryId: 'heater', name: 'Titanium 500W' },
  { id: 'brs-titanium-800', brandId: 'brs', subcategoryId: 'heater', name: 'Titanium 800W' },

  // === CHILLERS ===
  { id: 'teco-tk500', brandId: 'teco', subcategoryId: 'chiller', name: 'TK500' },
  { id: 'teco-tk1000', brandId: 'teco', subcategoryId: 'chiller', name: 'TK1000' },
  { id: 'teco-tk2000', brandId: 'teco', subcategoryId: 'chiller', name: 'TK2000' },
  { id: 'jbj-arctica-1-10', brandId: 'jbj', subcategoryId: 'chiller', name: 'Arctica 1/10 HP' },
  { id: 'jbj-arctica-1-5', brandId: 'jbj', subcategoryId: 'chiller', name: 'Arctica 1/5 HP' },
  { id: 'jbj-arctica-1-4', brandId: 'jbj', subcategoryId: 'chiller', name: 'Arctica 1/4 HP' },
  { id: 'aqua-euro-1-10', brandId: 'aqua_euro', subcategoryId: 'chiller', name: 'Max Chill 1/10 HP' },
  { id: 'aqua-euro-1-4', brandId: 'aqua_euro', subcategoryId: 'chiller', name: 'Max Chill 1/4 HP' },

  // === UV STERILIZERS ===
  { id: 'aqua-uv-15w', brandId: 'aqua_uv', subcategoryId: 'uv_sterilizer', name: 'Classic 15W' },
  { id: 'aqua-uv-25w', brandId: 'aqua_uv', subcategoryId: 'uv_sterilizer', name: 'Classic 25W' },
  { id: 'aqua-uv-40w', brandId: 'aqua_uv', subcategoryId: 'uv_sterilizer', name: 'Classic 40W' },
  { id: 'coralife-9w', brandId: 'coralife', subcategoryId: 'uv_sterilizer', name: 'Turbo-Twist 9W' },
  { id: 'coralife-18w', brandId: 'coralife', subcategoryId: 'uv_sterilizer', name: 'Turbo-Twist 18W' },
  { id: 'coralife-36w', brandId: 'coralife', subcategoryId: 'uv_sterilizer', name: 'Turbo-Twist 36W' },
  { id: 'icecap-uv-9w', brandId: 'icecap', subcategoryId: 'uv_sterilizer', name: '9W UV Sterilizer' },
  { id: 'icecap-uv-18w', brandId: 'icecap', subcategoryId: 'uv_sterilizer', name: '18W UV Sterilizer' },

  // === OZONE ===
  { id: 'ozotech-100mg', brandId: 'ozotech', subcategoryId: 'ozone', name: 'Poseidon 100mg' },
  { id: 'ozotech-200mg', brandId: 'ozotech', subcategoryId: 'ozone', name: 'Poseidon 200mg' },
  { id: 'ozotech-300mg', brandId: 'ozotech', subcategoryId: 'ozone', name: 'Poseidon 300mg' },
  { id: 'red-sea-ozone-25', brandId: 'red_sea', subcategoryId: 'ozone', name: 'Reef Spec Ozone 25' },
  { id: 'red-sea-ozone-50', brandId: 'red_sea', subcategoryId: 'ozone', name: 'Reef Spec Ozone 50' },
  { id: 'red-sea-ozone-100', brandId: 'red_sea', subcategoryId: 'ozone', name: 'Reef Spec Ozone 100' },
];
